import { Controller as BaseController } from 'stimulus'
import Highcharts from 'highcharts'
import { useDebounce } from 'stimulus-use'
import Rails from '@rails/ujs'
import { useBusItem } from '@app/javascript/controllers/mixins/useBusItem.js'
import I18n from '@app/javascript/i18n/index.js.erb'

export class Controller extends BaseController {
  static targets = [
    'self',
    'positionChart',
    'sentimentChart',
    'searchInput',
    'list',
    'loading',
    'exportButton',
    'selectAllCheckboxText',
    'selectAllCheckbox',
    'theme',
    'showThemesInput'
  ]

  static values = {
    commentPositions: Array,
    positionAnalysis: Object,
    sentimentPositions: Array,
    sentimentAnalysis: Object,
    filterUrl: String,
    exportUrl: String,
    filteredPosition: String,
    filteredSentiment: String,
    filteredQuery: String,
    totalComments: Number,
    maxCommentsForExport: Number
  }

  static debounces = ['triggerSearch']

  currentRequest = null

  selected = []
  visibleItems = []

  connect() {
    useBusItem(this)
    useDebounce(this, { wait: 500 })

    this.updatePositionGraph()
    this.updateSentimentGraph()
  }

  triggerSearch() {
    this.filteredQueryValue = this.searchInputTarget.value
    this.filter()
  }

  toggleThemes() {
    this.themeTargets.forEach(elem => elem.classList.toggle('hidden', !this.showThemesInputTarget.checked))
  }

  handlePositionFiltering(e) {
    this.filterPosition(e.currentTarget.dataset.position)
  }

  handleSentimentFiltering(e) {
    this.filterSentiment(e.currentTarget.dataset.sentiment)
  }

  filterPosition(position) {
    if (this.hasFilteredPositionValue && this.filteredPositionValue === position) {
      this.filteredPositionValue = ''
      this.filteredSentimentValue = ''
    } else {
      this.filteredPositionValue = position
    }

    this.filter()
  }

  filterSentiment(sentiment) {
    if (this.hasFilteredSentimentValue && this.filteredSentimentValue === sentiment) {
      this.filteredSentimentValue = ''
    } else {
      this.filteredSentimentValue = sentiment
    }

    this.filter()
  }

  resetPositionFiltering() {
    this.filteredPositionValue = null
    this.filteredSentimentValue = null
    this.filter()
  }

  filter() {
    if (this.currentRequest) {
      this.currentRequest.abort()
    }

    Rails.ajax({
      type: 'GET',
      dataType: 'script',
      url: this.filterUrlValue,
      beforeSend: (xhr) => {
        this.currentRequest = xhr
        this.listTarget.classList.add('hidden')
        this.loadingTarget.classList.remove('hidden')
        return true
      },
      data: $.param({
        target_element_id: this.selfTarget.id,
        render_raw: true,
        search_text: this.filteredQueryValue,
        position: this.filteredPositionValue,
        sentiment: this.filteredSentimentValue
      })
    })
  }

  itemsLoaded() {
    if (this.totalCommentsValue !== 0) {
      if (this.visibleItems.length === this.totalCommentsValue) {
        this.selected = []
        this.visibleItems = []
      }

      this.getVisibleItemsIds()
      this.updateSelectAllCheckboxState()
      this.updateSelectAllCheckboxText()
      this.updateExportButtonText()
      this.toggleThemes()
    }
  }

  getVisibleItemsIds() {
    const ids = []

    $('ul input[type=checkbox]').each(function() {
      ids.push($(this).attr('value'))
    })

    this.visibleItems = []
    this.visibleItems.push(...ids)
  }

  handleToggle(e) {
    if (e.detail.checked) {
      this.selected.push(e.detail.id)
    } else {
      this.selected.splice(this.selected.indexOf(e.detail.id), 1)
    }

    this.updateSelectAllCheckboxState()
    this.updateSelectAllCheckboxText()
    this.updateExportButtonText()
  }

  updateExportButtonText() {
    if (this.selected.length || (this.totalCommentsValue <= this.maxCommentsForExportValue)) {
      this.exportButtonTarget.innerHTML = I18n.t('entities.fed_reg_docket_comments.export_button',
        { count: this.selected.length || this.totalCommentsValue })
    } else {
      this.exportButtonTarget.innerHTML = I18n.t('entities.fed_reg_docket_comments.export_first_button',
        { count: this.maxCommentsForExportValue })
    }

    this.handleExportLimit()
  }

  updateSelectAllCheckboxText() {
    if (this.selected.length !== 0) {
      this.selectAllCheckboxTextTarget.innerHTML = I18n.t('entities.fed_reg_docket_comments.select_all_text_when_checked_html',
        { count: this.selected.length, total: this.totalCommentsValue })
    } else {
      this.selectAllCheckboxTextTarget.innerHTML = I18n.t('entities.fed_reg_docket_comments.select_all_text_when_unchecked_html',
        { count: this.visibleItems.length, total: this.totalCommentsValue })
    }
  }

  updateSelectAllCheckboxState() {
    this.selectAllCheckboxTarget.checked = this.selected.length === this.visibleItems.length
  }

  handleSelectAllToggle() {
    if (this.selectAllCheckboxTarget.checked === true) {
      this.selected = []
      this.selected.push(...this.visibleItems)

      this.selected.forEach((id) => {
        $(`input[type=checkbox][value=${id}]`).prop('checked', true)
      })
    } else {
      this.selected.forEach((id) => {
        $(`input[type=checkbox][value=${id}]`).prop('checked', false)
      })
      this.selected = []
    }

    this.updateSelectAllCheckboxState()
    this.updateSelectAllCheckboxText()
    this.updateExportButtonText()
  }

  handleExportLimit() {
    if (this.selected.length > this.maxCommentsForExportValue) {
      this.exportButtonTarget.setAttribute('disabled', 'true')
      this.exportButtonTarget.parentElement.removeAttribute('disabled')
    } else {
      this.exportButtonTarget.removeAttribute('disabled')
      this.exportButtonTarget.parentElement.setAttribute('disabled', 'true')
    }
  }

  setExportFormAttributes() {
    const exportController = this.getBusItem('export-generator')
    exportController.exportOriginValue = 'docketPage'
    exportController.savePathValue = this.exportUrlValue
    exportController.entityIdsValue = []
    exportController.searchAttributesValue = $.param({
      search_attributes: {
        entity_unique_ids: this.selected,
        search_text: this.filteredQueryValue,
        position: this.filteredPositionValue,
        total_comments: this.totalCommentsValue
      }
    })
    exportController.totalValue = this.selected.length
  }

  updatePositionGraph() {
    if (!this.hasPositionChartTarget) return

    Highcharts.chart(this.positionChartTarget, {
      chart: {
        type: 'bar',
        height: 32,
        margin: [0, 0, 0, 0]
      },
      colors: ['#D8D8D8', '#939598', '#e47800', '#2fa3ad'],
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      yAxis: {
        visible: false
      },
      xAxis: {
        visible: false
      },
      series: this.commentPositionsValue.map(key => {
        const options = { name: key, data: [this.positionAnalysisValue[key]], stacking: 'normal' }

        if (key !== 'none') {
          Object.assign(options, {
            cursor: 'pointer',
            events: {
              click: e => {
                this.filterPosition(e.point.series.name)
              }
            }
          })
        } else {
          Object.assign(options, { enableMouseTracking: false })
        }

        return options
      }).reverse()
    })
  }

  updateSentimentGraph() {
    if (!this.hasSentimentChartTarget) return

    Highcharts.chart(this.sentimentChartTarget, {
      chart: {
        type: 'bar',
        height: 32,
        margin: [0, 0, 0, 0]
      },
      colors: ['#F0C11C', '#939598', '#3AC6CD'],
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      yAxis: {
        visible: false
      },
      xAxis: {
        visible: false
      },
      series: this.sentimentPositionsValue.map(key => {
        const options = { name: key, data: [this.sentimentAnalysisValue[key]], stacking: 'normal' }

        Object.assign(options, {
          cursor: 'pointer',
          events: {
            click: e => {
              this.filterSentiment(e.point.series.name)
            }
          }
        })

        return options
      }).reverse()
    })
  }
}
